import React from 'react';
import { Search, Filter, Download, RefreshCw } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { DateRangePicker } from '@/components/ui/date-range-picker';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { UserFilters as UserFiltersType } from '@/services/admin/user-management-service';
import { DateRange } from 'react-day-picker';

interface UserFiltersProps {
  filters: UserFiltersType;
  onFiltersChange: (filters: UserFiltersType) => void;
  onExport: () => void;
  onRefresh: () => void;
  isLoading?: boolean;
}

export const UserFilters: React.FC<UserFiltersProps> = ({
  filters,
  onFiltersChange,
  onExport,
  onRefresh,
  isLoading = false
}) => {
  const handleSearchChange = (value: string) => {
    onFiltersChange({ ...filters, search: value });
  };

  const handleSubscriptionStatusChange = (value: string) => {
    onFiltersChange({ 
      ...filters, 
      subscriptionStatus: value === 'all' ? undefined : value 
    });
  };

  const handleActivityStatusChange = (value: string) => {
    onFiltersChange({ 
      ...filters, 
      activityStatus: value === 'all' ? undefined : value 
    });
  };

  const handleSourceChange = (value: string) => {
    onFiltersChange({ 
      ...filters, 
      source: value === 'all' ? undefined : value 
    });
  };

  const handleDateRangeChange = (dateRange: DateRange | undefined) => {
    onFiltersChange({
      ...filters,
      dateRange: dateRange?.from && dateRange?.to ? {
        from: dateRange.from,
        to: dateRange.to
      } : undefined
    });
  };

  const clearFilters = () => {
    onFiltersChange({});
  };

  return (
    <TooltipProvider>
      <div className="space-y-3">
        {/* Mobile Layout */}
        <div className="lg:hidden space-y-3">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search users..."
              value={filters.search || ''}
              onChange={(e) => handleSearchChange(e.target.value)}
              className="pl-10 h-11"
            />
          </div>

          {/* Filters Grid */}
          <div className="grid grid-cols-2 gap-2">
            <Select 
              value={filters.subscriptionStatus || 'all'} 
              onValueChange={handleSubscriptionStatusChange}
            >
              <SelectTrigger className="h-11">
                <SelectValue placeholder="Subscription" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Plans</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="expired">Expired</SelectItem>
                <SelectItem value="cancelled">Cancelled</SelectItem>
              </SelectContent>
            </Select>

            <Select 
              value={filters.activityStatus || 'all'} 
              onValueChange={handleActivityStatusChange}
            >
              <SelectTrigger className="h-11">
                <SelectValue placeholder="Activity" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="inactive">Inactive</SelectItem>
              </SelectContent>
            </Select>

            <Select 
              value={filters.source || 'all'} 
              onValueChange={handleSourceChange}
            >
              <SelectTrigger className="h-11">
                <SelectValue placeholder="Source" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="organic">Organic</SelectItem>
                <SelectItem value="affiliate">Affiliate</SelectItem>
              </SelectContent>
            </Select>

            <DateRangePicker 
              dateRange={{
                from: filters.dateRange?.from,
                to: filters.dateRange?.to
              }}
              onUpdate={handleDateRangeChange}
              className="col-span-1"
            />
          </div>

          {/* Actions */}
          <div className="flex gap-2 justify-center">
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={clearFilters}
                  className="h-11 w-11 p-0"
                >
                  <Filter className="h-5 w-5" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Clear filters</TooltipContent>
            </Tooltip>
            
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={onRefresh}
                  disabled={isLoading}
                  className="h-11 w-11 p-0"
                >
                  <RefreshCw className={`h-5 w-5 ${isLoading ? 'animate-spin' : ''}`} />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Refresh data</TooltipContent>
            </Tooltip>
            
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={onExport}
                  className="h-11 w-11 p-0"
                >
                  <Download className="h-5 w-5" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Export users</TooltipContent>
            </Tooltip>
          </div>
        </div>

        {/* Desktop Layout */}
        <div className="hidden lg:flex flex-col lg:flex-row gap-3 items-start lg:items-center justify-between">
          <div className="flex flex-col sm:flex-row gap-2 flex-1 max-w-4xl">
            {/* Search */}
            <div className="relative flex-1 max-w-xs">
              <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search users..."
                value={filters.search || ''}
                onChange={(e) => handleSearchChange(e.target.value)}
                className="pl-8 h-9"
              />
            </div>

            {/* Filters Row */}
            <div className="flex gap-2 flex-wrap">
              <Select 
                value={filters.subscriptionStatus || 'all'} 
                onValueChange={handleSubscriptionStatusChange}
              >
                <SelectTrigger className="w-[140px] h-9">
                  <SelectValue placeholder="Subscription" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Plans</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="expired">Expired</SelectItem>
                  <SelectItem value="cancelled">Cancelled</SelectItem>
                </SelectContent>
              </Select>

              <Select 
                value={filters.activityStatus || 'all'} 
                onValueChange={handleActivityStatusChange}
              >
                <SelectTrigger className="w-[120px] h-9">
                  <SelectValue placeholder="Activity" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="inactive">Inactive</SelectItem>
                </SelectContent>
              </Select>

              <Select 
                value={filters.source || 'all'} 
                onValueChange={handleSourceChange}
              >
                <SelectTrigger className="w-[120px] h-9">
                  <SelectValue placeholder="Source" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="organic">Organic</SelectItem>
                  <SelectItem value="affiliate">Affiliate</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-2 items-center">
            <DateRangePicker 
              dateRange={{
                from: filters.dateRange?.from,
                to: filters.dateRange?.to
              }}
              onUpdate={handleDateRangeChange}
            />
            
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={clearFilters}
                  className="h-9 px-3"
                >
                  <Filter className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Clear filters</TooltipContent>
            </Tooltip>
            
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={onRefresh}
                  disabled={isLoading}
                  className="h-9 px-3"
                >
                  <RefreshCw className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Refresh data</TooltipContent>
            </Tooltip>
            
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={onExport}
                  className="h-9 px-3"
                >
                  <Download className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Export users</TooltipContent>
            </Tooltip>
          </div>
        </div>
      </div>
    </TooltipProvider>
  );
};